
import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Building, Globe, Users, Shield, Check, Download } from "lucide-react";
import { Link } from "react-router-dom";

const CaseStudies = () => {
  // Sample case studies data
  const caseStudies = [
    {
      id: "1",
      title: "Global Financial Institution Reduces Security Alerts by 95%",
      company: "Major Financial Services Provider",
      industry: "Financial Services",
      challenge: "Managing over 10,000 daily security alerts with limited staff",
      solution: "Implemented Spharaka XDR with Agentic AI for autonomous alert triage and investigation",
      results: [
        "95% reduction in security alerts requiring human review",
        "82% faster mean time to detect (MTTD) for critical threats",
        "76% reduction in mean time to respond (MTTR)",
        "Eliminated alert fatigue among security analysts"
      ],
      logo: "/lovable-uploads/aad0c645-89f5-4dd6-bf0d-04384e55a2bf.png",
      image: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=1600&q=80"
    },
    {
      id: "2",
      title: "Healthcare Provider Strengthens Ransomware Defense",
      company: "Regional Healthcare Network",
      industry: "Healthcare",
      challenge: "Protecting sensitive patient data from sophisticated ransomware attacks",
      solution: "Deployed Spharaka XDR across 15,000 endpoints and cloud workloads",
      results: [
        "Prevented 3 attempted ransomware attacks in first 6 months",
        "99.7% detection rate for malicious activities",
        "Automated containment of compromised assets within minutes",
        "Reduced security operations costs by 40%"
      ],
      logo: "/lovable-uploads/aad0c645-89f5-4dd6-bf0d-04384e55a2bf.png",
      image: "https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7?auto=format&fit=crop&w=1600&q=80"
    },
    {
      id: "3",
      title: "Manufacturing Giant Secures Global Operations",
      company: "Global Manufacturing Leader",
      industry: "Manufacturing",
      challenge: "Securing diverse OT/IT environments across 50+ global locations",
      solution: "Implemented Spharaka XDR with custom policies for OT/IT environments",
      results: [
        "Unified security visibility across previously siloed environments",
        "90% faster detection of threats targeting industrial systems",
        "Automated response actions for common threat scenarios",
        "Improved compliance reporting efficiency by 65%"
      ],
      logo: "/lovable-uploads/aad0c645-89f5-4dd6-bf0d-04384e55a2bf.png",
      image: "https://images.unsplash.com/photo-1487887235947-a955ef187fcc?auto=format&fit=crop&w=1600&q=80"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Customer <span className="text-[#8932c8]">Success Stories</span></h1>
              <p className="text-xl text-gray-600 mb-10">
                Discover how organizations across industries have transformed their security operations with Spharaka XDR.
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                <Button className="bg-[#8932c8] hover:bg-[#7a2cb3]">
                  View All Case Studies
                </Button>
                <Button variant="outline" className="border-[#8932c8] text-[#8932c8] hover:bg-[#8932c8]/5">
                  Request a Demo
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Case Study */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="mb-6">
                  <span className="text-[#8932c8] font-semibold">{caseStudies[0].industry}</span>
                  <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-4">
                    {caseStudies[0].title}
                  </h2>
                  <p className="text-lg text-gray-600">
                    {caseStudies[0].company}
                  </p>
                </div>

                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-2">Challenge</h3>
                  <p className="text-gray-600">
                    {caseStudies[0].challenge}
                  </p>
                </div>

                <div className="mb-6">
                  <h3 className="font-semibold text-lg mb-2">Solution</h3>
                  <p className="text-gray-600">
                    {caseStudies[0].solution}
                  </p>
                </div>

                <div className="mb-8">
                  <h3 className="font-semibold text-lg mb-2">Results</h3>
                  <ul className="space-y-2">
                    {caseStudies[0].results.map((result, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-gray-600">{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Button className="flex items-center gap-2 bg-[#8932c8] hover:bg-[#7a2cb3]">
                  <Download className="h-4 w-4" />
                  Download Full Case Study
                </Button>
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg">
                <img 
                  src={caseStudies[0].image} 
                  alt={caseStudies[0].title} 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* More Case Studies */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold mb-12 text-center">More Success Stories</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {caseStudies.slice(1).map((study) => (
                <div key={study.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="h-56 overflow-hidden">
                    <img 
                      src={study.image} 
                      alt={study.title} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <span className="text-sm font-medium bg-[#8932c8]/10 text-[#8932c8] px-3 py-1 rounded-full">
                        {study.industry}
                      </span>
                    </div>
                    <h3 className="font-bold text-xl mb-2">{study.title}</h3>
                    <p className="text-gray-600 mb-6">{study.company}</p>
                    
                    <div className="space-y-4 mb-6">
                      <div>
                        <p className="font-medium">Challenge:</p>
                        <p className="text-gray-600 text-sm">{study.challenge}</p>
                      </div>
                      <div>
                        <p className="font-medium">Results:</p>
                        <p className="text-gray-600 text-sm">{study.results[0]}</p>
                      </div>
                    </div>
                    
                    <Button variant="ghost" className="text-[#8932c8] hover:text-[#7a2cb3] hover:bg-[#8932c8]/5 p-0">
                      Read Full Case Study →
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                We Serve All <span className="text-[#8932c8]">Industries</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Spharaka XDR is trusted by organizations across all sectors to protect their most critical assets.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 text-center">
                <div className="w-12 h-12 bg-[#8932c8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building className="h-6 w-6 text-[#8932c8]" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Financial Services</h3>
                <p className="text-gray-600">
                  Protecting sensitive financial data and transaction systems from advanced threats.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 text-center">
                <div className="w-12 h-12 bg-[#8932c8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-6 w-6 text-[#8932c8]" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Healthcare</h3>
                <p className="text-gray-600">
                  Securing patient data and critical healthcare systems while maintaining compliance.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 text-center">
                <div className="w-12 h-12 bg-[#8932c8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="h-6 w-6 text-[#8932c8]" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Manufacturing</h3>
                <p className="text-gray-600">
                  Protecting operational technology and IT environments from targeted attacks.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#8932c8]">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center text-white">
              <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Security Operations?</h2>
              <p className="text-lg mb-8 text-white/90">
                Join the growing list of organizations that have revolutionized their security with Spharaka XDR.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button className="bg-white text-[#8932c8] hover:bg-gray-100">
                  Request a Demo
                </Button>
                <Button variant="outline" className="border-white text-white hover:bg-white/10">
                  Contact Sales
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CaseStudies;
